import { useParams, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { AssociatedTasks } from '../Tasks/AssociatedTasks';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getLead, markLeadRead } from '@/services/api';
import { associateLeadWithClient, updateLeadStatus } from '@/services/leadsApi';
import { updateAccount } from '@/services/accountsApi';
import { useClientMutation } from '@/features/clients/hooks/useClientMutation';
import { useAuth } from '@/services/auth';
import { LEAD_PRIORITY_LABELS } from '@/types';
import type { LeadPriority } from '@/types';
import type { CreateClientFormData } from '@/components/forms/CreateClientForm';
import type { EditClientFormData } from '@/components/forms/EditClientForm';
import ClientInfoBox from '@/components/relationships/ClientInfoBox';
import AssignOwnerLead from './components/AssignOwnerLead';
import LeadReplyComposer from './components/LeadReplyComposer';
import LeadRepliesThread from './components/LeadRepliesThread';
import InternalNotesPanelLead from './components/InternalNotesPanelLead';
import StatusDropdown, { getLeadStatusColor } from '@/components/shared/StatusDropdown';
import PromoteToProspectPanel from '@/features/prospects/components/PromoteToProspectPanel';
import {
  ArrowTrendingUpIcon,
  ChevronLeftIcon,
  ClockIcon,
  FlagIcon,
  IdentificationIcon,
  MegaphoneIcon,
  PencilSquareIcon,
  UserCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

function getLeadPriorityPillClass(priority: LeadPriority): string {
  if (priority >= 3) {
    return 'bg-red-100 text-red-700';
  }
  if (priority === 2) {
    return 'bg-yellow-100 text-yellow-700';
  }
  return 'bg-green-100 text-green-700';
}

export default function LeadDetail() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const { user: _user } = useAuth();
  // Bug 1/2 (Feature 039): snapshot of last-read time before mark-read overwrites it.
  const [lastReadAt, setLastReadAt] = useState<string | null | undefined>(undefined);
  const [isPromoteModalOpen, setIsPromoteModalOpen] = useState(false);

  const { data: lead, isLoading, isError } = useQuery({
    queryKey: ['lead', id],
    queryFn: () => getLead(id!),
    enabled: !!id,
  });

  // US2 (Feature 039): Mark lead as read on mount; capture previousLastReadAt for thread highlights.
  // Surgically flip isUnread=false in the cached list instead of invalidating the full list —
  // this prevents customerResponded from being re-evaluated until the SDR actually sends a reply.
  useEffect(() => {
    if (!id) return;
    markLeadRead(id).then(({ previousLastReadAt }) => {
      setLastReadAt(previousLastReadAt);
      queryClient.setQueriesData<{ items: any[]; total: number }>(
        { queryKey: ['leads'] },
        (old) => {
          if (!old?.items) return old;
          return {
            ...old,
            items: old.items.map((l) =>
              l.leadId === id ? { ...l, isUnread: false } : l
            ),
          };
        }
      );
    });
  }, [id, queryClient]);

  // Feature 018 US2: Client creation mutation
  const createClientMutation = useClientMutation();

  // Feature 018: Client association mutation with optimistic updates
  const associateMutation = useMutation({
    mutationFn: ({ clientId, etag }: { clientId: string; etag?: string }) =>
      associateLeadWithClient(id!, clientId, etag),
    onSuccess: () => {
      // Invalidate and refetch lead data to get updated clientInfo
      queryClient.invalidateQueries({ queryKey: ['lead', id] });
    },
    onError: (error: any) => {
      alert(error?.response?.data?.message || 'Failed to associate account. Please try again.');
    },
  });

  // Feature 018: Unassociate client mutation
  const unassociateMutation = useMutation({
    mutationFn: ({ etag }: { etag?: string }) =>
      associateLeadWithClient(id!, null, etag),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lead', id] });
    },
    onError: (error: any) => {
      alert(error?.response?.data?.message || 'Failed to remove account association. Please try again.');
    },
  });

  const handleAssociate = async (clientId: string) => {
    await associateMutation.mutateAsync({ clientId });
  };

  const handleCreate = async (data: CreateClientFormData) => {
    const result = await createClientMutation.mutateAsync({
      companyName: data.companyName,
      firstName: data.firstName,
      lastName: data.lastName,
      contactName: `${data.firstName} ${data.lastName}`, // Auto-generate contact name
      email: data.email,
      phone: data.phone,
      country: data.country,
      city: data.city || undefined,
      street: data.street || undefined,
      clientSegment: data.clientSegment, // Feature 019: Include client segment
    });

    return {
      clientId: result.account.accountId,
      warning: result.warning,
    };
  };

  const handleEdit = async (clientId: string, data: EditClientFormData) => {
    // Map frontend status (ACTIVE/ARCHIVED) to backend enum (Active/Archived)
    const backendStatus = data.status === 'ACTIVE' ? 'Active' : 'Archived';
    
    await updateAccount(clientId, {
      companyName: data.companyName,
      firstName: data.firstName,
      lastName: data.lastName,
      contactName: `${data.firstName} ${data.lastName}`, // Auto-generate contact name
      email: data.email,
      phone: data.phone || '',
      country: data.country,
      city: data.city || '',
      street: data.street || '',
      status: backendStatus as any, // Feature 019: Include status
      clientSegment: data.clientSegment, // Feature 019: Include client segment
    });

    // Refetch lead data to show updated client info
    queryClient.invalidateQueries({ queryKey: ['lead', id] });
  };

  const handleRemove = async () => {
    await unassociateMutation.mutateAsync({});
  };

  // Feature 019 US2/P2: Status update mutation with optimistic updates
  const statusMutation = useMutation({
    mutationFn: async (newStatus: string) => {
      return updateLeadStatus(id!, newStatus);
    },
    onMutate: async (newStatus) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['lead', id] });

      // Snapshot previous value
      const previousLead = queryClient.getQueryData(['lead', id]);

      // Optimistically update to new value
      queryClient.setQueryData(['lead', id], (old: any) => ({
        ...old,
        status: newStatus,
      }));

      return { previousLead };
    },
    onError: (_err, _newStatus, context) => {
      // Revert on error
      queryClient.setQueryData(['lead', id], context?.previousLead);
      alert('Failed to update status. Please try again.');
    },
    onSuccess: () => {
      // Invalidate queries to refetch fresh data
      queryClient.invalidateQueries({ queryKey: ['lead', id] });
      queryClient.invalidateQueries({ queryKey: ['leads'] });
    },
  });

  const handleStatusUpdate = async (newStatus: string) => {
    await statusMutation.mutateAsync(newStatus);
  };

  if (isLoading) return <p className="text-brand-text-secondary">Loading…</p>;
  // T018 (FR-001): fail-closed fallback — if the API call errors, treat the lead as read-only
  // so no action controls are inadvertently exposed.
  if (isError) {
    return (
      <div role="alert" className="mx-auto max-w-[78rem] px-2 sm:px-4">
        <div className="mt-8 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <ArrowTrendingUpIcon className="h-5 w-5 shrink-0 text-amber-600" aria-hidden="true" />
          <span>Editing is unavailable. Please try again later.</span>
        </div>
      </div>
    );
  }
  if (!lead) return <p className="text-red-500">Lead not found.</p>;

  // T016 (FR-001): derive read-only state from PromotedAt.
  // isReadOnly is true when the lead has been promoted — it is permanently true and never flips back.
  const isReadOnly = !!lead.promotedAt;

  const leadPriority = Number(lead.priority) as LeadPriority;
  const leadPriorityLabel = LEAD_PRIORITY_LABELS[leadPriority] || lead.priority;

  return (
    <div className="mx-auto max-w-[78rem] px-2 sm:px-4">
      {/* T017 (FR-001): Read-only banner shown when the lead has been promoted */}
      {isReadOnly && (
        <div
          role="alert"
          className="mb-4 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"
        >
          <ArrowTrendingUpIcon className="h-5 w-5 shrink-0 text-amber-600" aria-hidden="true" />
          <span>This lead has been promoted to a Prospect and is read-only.</span>
        </div>
      )}

      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link
            to="/leads"
            title="Back to Leads"
            aria-label="Back to Leads"
            className="rounded-md border border-brand-border bg-brand-surface-card p-2 text-brand-text-secondary transition hover:bg-brand-surface"
          >
            <ChevronLeftIcon className="h-5 w-5" title="Back to Leads" />
          </Link>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold text-brand-text-primary">Lead</h1>
            <span className="font-mono text-sm text-brand-text-secondary">{lead.internalId}</span>
          </div>
        </div>
        {/* T016 (FR-001): Hide Promote and Edit buttons for read-only (promoted) leads */}
        {!isReadOnly && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setIsPromoteModalOpen(true)}
              title="Promote to Prospect"
              aria-label="Promote to Prospect"
              className="rounded-full border border-brand-border bg-brand-surface-card p-3 text-brand-text-primary transition hover:bg-brand-surface"
            >
              <ArrowTrendingUpIcon className="h-5 w-5" title="Promote to Prospect" />
            </button>
            <Link
              to={`/leads/${lead.leadId}/edit`}
              title="Edit Lead"
              aria-label="Edit Lead"
              className="rounded-full border border-brand-border bg-brand-filter-tab-active-bg p-3 text-brand-filter-tab-active-text transition hover:opacity-85"
            >
              <PencilSquareIcon className="h-5 w-5" title="Edit Lead" />
            </Link>
          </div>
        )}
      </div>

      {/* Client Information - Feature 018: Editable client association */}
      <ClientInfoBox
        clientInfo={lead.clientInfo}
        onAssociate={handleAssociate}
        onCreate={handleCreate}
        onEdit={handleEdit}
        onRemove={handleRemove}
        isLoading={associateMutation.isPending || unassociateMutation.isPending || createClientMutation.isPending}
        className="mb-6"
      />

      {/* Subject and overview with right-side controls */}
      <div className="mb-5 grid grid-cols-1 gap-4 xl:grid-cols-3">
        <section className="xl:col-span-2 space-y-4">
          <div className="rounded-xl border border-brand-border bg-white p-5 shadow-sm">
            <h2 className="mb-1 text-xs font-semibold uppercase tracking-wide text-brand-text-secondary">Subject</h2>
            <p className="text-xl font-semibold text-brand-text-primary">{lead.subject || 'No subject provided'}</p>
            <h3 className="mb-1 mt-4 text-xs font-semibold uppercase tracking-wide text-brand-text-secondary">Details</h3>
            <p className="whitespace-pre-wrap text-sm text-brand-text-secondary">{lead.description || 'No details provided.'}</p>
          </div>

          <div className="rounded-xl border border-brand-border bg-white p-5 shadow-sm">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-brand-text-primary">Lead Data</h2>
              <span className="text-xs text-brand-text-secondary">Lead attributes</span>
            </div>

            <div className="grid grid-cols-1 gap-3 text-sm md:grid-cols-2">
              <div className="rounded-lg border border-brand-border bg-brand-surface p-3">
                <div className="mb-1 flex items-center gap-1.5 text-xs text-brand-text-secondary">
                  <MegaphoneIcon className="h-4 w-4" title="Lead Source" /> Lead Source
                </div>
                <strong className="text-brand-text-primary">{lead.leadSource || '—'}</strong>
              </div>

              <div className="rounded-lg border border-brand-border bg-brand-surface p-3">
                <div className="mb-1 flex items-center gap-1.5 text-xs text-brand-text-secondary">
                  <IdentificationIcon className="h-4 w-4" title="Lead ID" /> Lead ID
                </div>
                <span className="font-mono text-xs">{lead.internalId || '—'}</span>
              </div>

              <div className="rounded-lg border border-brand-border bg-brand-surface p-3">
                <div className="mb-1 flex items-center gap-1.5 text-xs text-brand-text-secondary">
                  <FlagIcon className="h-4 w-4" title="Priority" /> Priority
                </div>
                <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${getLeadPriorityPillClass(leadPriority)}`}>
                  {leadPriorityLabel}
                </span>
              </div>

              <div className="rounded-lg border border-brand-border bg-brand-surface p-3">
                <div className="mb-1 flex items-center gap-1.5 text-xs text-brand-text-secondary">
                  <ClockIcon className="h-4 w-4" title="Created" /> Created
                </div>
                {new Date(lead.createdAt).toLocaleString()}
              </div>

              <div className="rounded-lg border border-brand-border bg-brand-surface p-3">
                <div className="mb-1 flex items-center gap-1.5 text-xs text-brand-text-secondary">
                  <ClockIcon className="h-4 w-4" title="Updated" /> Updated
                </div>
                {new Date(lead.updatedAt).toLocaleString()}
              </div>

              {lead.qualifiedAt && (
                <div className="rounded-lg border border-brand-border bg-brand-surface p-3">
                  <div className="mb-1 flex items-center gap-1.5 text-xs text-brand-text-secondary">
                    <ClockIcon className="h-4 w-4" title="Qualified" /> Qualified
                  </div>
                  {new Date(lead.qualifiedAt).toLocaleString()}
                </div>
              )}

              {lead.closedAt && (
                <div className="rounded-lg border border-brand-border bg-brand-surface p-3">
                  <div className="mb-1 flex items-center gap-1.5 text-xs text-brand-text-secondary">
                    <ClockIcon className="h-4 w-4" title="Closed" /> Closed
                  </div>
                  {new Date(lead.closedAt).toLocaleString()}
                </div>
              )}
            </div>
          </div>
        </section>

        <aside className="xl:col-span-1">
          <div className="rounded-xl border border-brand-border bg-white p-5 shadow-sm xl:sticky xl:top-4">
            <h2 className="mb-4 text-lg font-semibold text-brand-text-primary">Controls</h2>
            <div className="space-y-4">
              <div className="rounded-lg border border-brand-border bg-brand-surface p-3">
                <div className="mb-2 flex items-center gap-1.5 text-xs text-brand-text-secondary">
                  <FlagIcon className="h-4 w-4" title="Status" /> Status
                </div>
                {/* T016/T020 (FR-001): Promoted leads show a static badge; non-promoted leads show the dropdown */}
                {isReadOnly ? (
                  <span className="inline-flex items-center rounded-full bg-violet-100 px-2.5 py-1 text-xs font-semibold text-violet-800">
                    Prospect
                  </span>
                ) : (
                  <StatusDropdown
                    currentStatus={lead.status}
                    options={['New', 'Contacted', 'Qualified', 'Lost'] as const}
                    onUpdate={handleStatusUpdate}
                    disabled={statusMutation.isPending}
                    getStatusColor={getLeadStatusColor}
                  />
                )}
              </div>

              {/* T016 (FR-001): Hide Owner assign control for read-only (promoted) leads */}
              {!isReadOnly && (
              <div className="rounded-lg border border-brand-border bg-brand-surface p-3">
                <div className="mb-2 flex items-center gap-1.5 text-xs text-brand-text-secondary">
                  <UserCircleIcon className="h-4 w-4" title="Owner" /> Owner
                </div>
                <AssignOwnerLead leadId={lead.leadId} currentOwnerUserId={lead.ownerUserId} />
              </div>
              )}
            </div>
          </div>
        </aside>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 xl:grid-cols-3">
        <section className="xl:col-span-2 space-y-4">
          {/* T016 (FR-001): Hide reply composer for read-only (promoted) leads */}
          {!isReadOnly && <LeadReplyComposer leadId={lead.leadId} />}
          <LeadRepliesThread
            leadId={lead.leadId}
            lastReadAt={lastReadAt}
            contactName={lead.contactName ?? undefined}
          />
        </section>

        <aside className="xl:col-span-1 space-y-4">
          <AssociatedTasks
              entityId={lead.leadId}
              entityType="lead"
              entityName={lead.subject || "Lead"}
          />
          <InternalNotesPanelLead leadId={lead.leadId} />
        </aside>
      </div>

      {isPromoteModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-4" role="dialog" aria-modal="true" aria-label="Promote to Prospect">
          <div className="w-full max-w-xl rounded-2xl border border-brand-border bg-white p-5 shadow-2xl">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-brand-text-primary">Promote to Prospect</h3>
              <button
                type="button"
                onClick={() => setIsPromoteModalOpen(false)}
                title="Close"
                aria-label="Close"
                className="rounded-md border border-brand-border p-1.5 text-brand-text-secondary hover:bg-brand-surface"
              >
                <XMarkIcon className="h-4 w-4" title="Close" />
              </button>
            </div>

            <PromoteToProspectPanel
              leadId={lead.leadId}
              onPromoted={() => setIsPromoteModalOpen(false)}
              showTitle={false}
            />
          </div>
        </div>
      )}
    </div>
  );
}
